import type { Layer, SourceSite } from "../../core/events";
import type { Theme } from "../../core/theme";
import { layerLabel } from "../../core/timeline";
import { CodeEditor } from "./CodeEditor";

/** A click-to-source request; each click makes a new object, which flashes again. */
export interface Reveal {
  layer: number;
  line: number;
}

interface EditorPanelProps {
  /** Remounts the main editor (e.g. when an example is picked). */
  docKey: string;
  initialCode: string;
  theme: Theme;
  layers: Layer[];
  /** The shown tab: 0 = main.js, otherwise a layer id. */
  tab: number;
  onTab: (tab: number) => void;
  reveal: Reveal | null;
  hover: SourceSite | null;
  onChange: (code: string) => void;
  onRun: () => void;
}

/**
 * The editor with its tab bar (docs/ui-design.md §1): the editable `main.js`
 * plus one read-only tab per revealed code layer, sliding in as the run
 * reveals them. The main editor stays mounted while a layer is shown, so its
 * undo history and scroll position survive.
 */
export function EditorPanel(props: EditorPanelProps) {
  const { layers, tab, onTab, reveal, hover, theme } = props;
  const layer = layers.find((l) => l.id === tab);
  const revealIn = (id: number) => (reveal && reveal.layer === id ? reveal : null);
  const hoverIn = (id: number) => (hover && hover.layer === id ? hover.line : null);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        role="tablist"
        aria-label="Code"
        data-testid="editor-tabs"
        className="flex gap-1 overflow-x-auto border-b border-border bg-bg-dark px-2 font-mono text-xs"
      >
        <EditorTab id={0} label="main.js" selected={tab === 0} onSelect={onTab} />
        {layers.map((l) => (
          <EditorTab
            key={l.id}
            id={l.id}
            label={layerLabel(l)}
            selected={tab === l.id}
            onSelect={onTab}
            reveal
          />
        ))}
      </div>
      <div
        id="editor-panel-0"
        role="tabpanel"
        aria-labelledby="editor-tab-0"
        hidden={tab !== 0}
        className="min-h-0 min-w-0 flex-1"
      >
        <CodeEditor
          key={props.docKey}
          initialValue={props.initialCode}
          theme={theme}
          label="main.js"
          onChange={props.onChange}
          onRun={props.onRun}
          reveal={revealIn(0)}
          hoverLine={hoverIn(0)}
        />
      </div>
      {layer && (
        <div
          id={`editor-panel-${layer.id}`}
          role="tabpanel"
          aria-labelledby={`editor-tab-${layer.id}`}
          className="min-h-0 min-w-0 flex-1"
        >
          <CodeEditor
            key={layer.id}
            initialValue={layer.code}
            theme={theme}
            label={layerLabel(layer)}
            readOnly
            onRun={props.onRun}
            reveal={revealIn(layer.id)}
            hoverLine={hoverIn(layer.id)}
            testId="layer-editor"
          />
        </div>
      )}
    </div>
  );
}

function EditorTab({
  id,
  label,
  selected,
  onSelect,
  reveal = false,
}: {
  id: number;
  label: string;
  selected: boolean;
  onSelect: (id: number) => void;
  reveal?: boolean;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={`editor-tab-${id}`}
      aria-selected={selected}
      aria-controls={`editor-panel-${id}`}
      data-layer={id}
      onClick={() => onSelect(id)}
      className={`shrink-0 whitespace-nowrap border-b-2 px-2 py-1 ${reveal ? "layer-tab-in" : ""} ${
        selected ? "border-blue text-fg" : "border-transparent text-fg-dark hover:text-fg"
      }`}
    >
      {label}
    </button>
  );
}
